import * as CL from '@design-system/component-library';
import { type JSX, useEffect, useState } from 'react';
import { isRouteErrorResponse, useLocation, useNavigation, useRevalidator, useRouteError } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { getHomePath, getPortal } from '../../common/constants/pathVariables.js';
import { CommonErrorType } from '../../common/enums';
import { fetchPublicPage } from '../../common/fetch.js';
import { NOINDEX_NOFOLLOW, ROBOTS_META_SELECTOR, setElementBySelectorAndContent } from '../../common/hooks/useMeta.js';
import { Locale, switchingCompanyToMsg, t } from '../../common/i18n/index.js';
import { isObject } from '../../common/utils/objectUtils.js';
import { EmptyOrError, getGenericSystemError } from '../../components/EmptyOrError/index.js';
import { EoeOutlet } from '../../components/EoeOutlet/EoeOutlet';
import { Header } from '../../components/Header/Header.js';
import { SelfServiceLogin } from '../../components/SelfServiceLoginScene/SelfServiceLogin.js';
import { SystemError } from '../../components/SystemError/SystemError';
import { useAuth } from './AuthProvider.js';
import { Maintenance } from './Maintenance.js';
import { PublicPageContent } from './path/PublicPath.js';
import type { PageResponse } from '../../generated/api/models.js';

export const SUPPORTED_SITE_LANGUAGES = {
  en: Locale.EN,
  fi: Locale.FI,
  sv: Locale.SV,
};

export const Spinner = () => (
  <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.PADDING_8} ${dsClass.JUSTIFY_CONTENT_CENTER}`}>
    <CL.LoadingSpinner size="xl" />
  </div>
);

export interface SpinnerWithSwitchAccountInfoTextProps {
  switchToAccountName: string;
}

export const SpinnerWithSwitchAccountInfoText = ({ switchToAccountName }: SpinnerWithSwitchAccountInfoTextProps) => (
  <>
    <div
      className={`${dsClass.DISPLAY_FLEX} ${dsClass.PADDING_TOP_10} ${dsClass.PADDING_BOTTOM_4} ${dsClass.JUSTIFY_CONTENT_CENTER}`}
    >
      <CL.LoadingSpinner size="xl" />
    </div>
    <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.JUSTIFY_CONTENT_CENTER}`}>
      <span className={dsClass.TEXT_LARGE}>
        {t.IGKK(switchingCompanyToMsg)} <b>{switchToAccountName}</b> {t.HQ7C('')}
      </span>
    </div>
  </>
);

/**
 * Responsible for loading spinner when moving to other pages through the SPA links
 * Not responsible for the initial loading spinner after full page load
 */
export const DataLoader = ({ children }: { children?: JSX.Element }) => {
  const isLoading = useNavigation().state === 'loading';
  if (isLoading) {
    return <Spinner />;
  }
  return children || <></>;
};

const NotFoundPage = () => {
  const [page, setPage] = useState<PageResponse | null>(null);
  useEffect(() => {
    setElementBySelectorAndContent(ROBOTS_META_SELECTOR, NOINDEX_NOFOLLOW);
    fetchPublicPage('/404').then(res => setPage(res));
  }, []);

  return (
    <>
      <Header />
      {page ? <PublicPageContent {...page} /> : <Spinner />}
    </>
  );
};

const GenericErrorPage = () => {
  return <EmptyOrError {...getGenericSystemError()} />;
};

const isAuthError = (error: unknown) => isRouteErrorResponse(error) && error.status === 401;

export interface AuthErrorBoundaryProps {
  isEoe: boolean;
}

export const AuthErrorBoundary = ({ isEoe }: AuthErrorBoundaryProps) => {
  const error = useRouteError();
  const revalidator = useRevalidator();
  const { isAuthenticated, isLoading, ssoSessionValid } = useAuth();

  useEffect(() => {
    if (isAuthError(error) && (isAuthenticated || ssoSessionValid) && !isLoading) {
      // Auth state has changed, presumably user has logged in, let's revalidate.
      // TODO: leo 30.5.2024 - Ideally the revalidation should happen directly due to user interaction, pressing the login button, but that's not feasible until Redux is removed.
      revalidator.revalidate();
    }
  }, [error, isAuthenticated, isLoading, ssoSessionValid, revalidator]);

  if (isAuthError(error)) {
    if (isEoe) {
      return <EoeOutlet />;
    } else {
      return <SelfServiceLogin />;
    }
  }

  throw error; // Let the error bubble.
};

/**
 * Errors will bubble up through parent routes so this can be given to multiple routes
 * If below nested route has the errorElement in it, it will be rendered instead of the above errorElement
 * Above routes must have the Outlet in order to render other content with the error like header or footer
 *
 * NOTE: Only use this in the route tree above `AuthErrorBoundary`, below it use `LeafErrorBoundary`.
 */
export const RootErrorBoundary = () => {
  const error = useRouteError();
  const { pathname } = useLocation();
  const portal = getPortal(pathname);

  if (isObject(error) && 'status' in error && error.status === 503) {
    return (
      <>
        <Header cmsPage isFullVersion={false} />
        <Maintenance />
      </>
    );
  }

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      if (portal === 'Public' || portal === 'OpenForm') {
        return <NotFoundPage />;
      } else {
        const homePath = getHomePath(pathname);
        return <SystemError errors={[{ type: CommonErrorType.NOT_FOUND, message: '' }]} homePath={homePath} />;
      }
    }
  }
  return <GenericErrorPage />;
};

/**
 * NOTE: Only use this in the route tree below `AuthErrorBoundary`, above it use `RootErrorBoundary`.
 */
export const LeafErrorBoundary = () => {
  const error = useRouteError();
  if (isAuthError(error)) {
    throw error; // Let the error bubble.
  } else {
    return <RootErrorBoundary />;
  }
};
